import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '../Button';
import { useExport } from '../../hooks/useExport';
import { DocumentType } from '../../api/client';

const ALL_DOCUMENT_TYPES: DocumentType[] = [
  'Receipt',
  'ProofOfPayment',
  'LetterOfMedicalNecessity',
  'ExplanationOfBenefits',
  'Other',
];

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  Receipt: 'Receipts',
  ProofOfPayment: 'Proof of Payment',
  LetterOfMedicalNecessity: 'Letter of Medical Necessity',
  ExplanationOfBenefits: 'Explanation of Benefits',
  Other: 'Other Documents',
};

export function Export() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedDocTypes, setSelectedDocTypes] = useState<DocumentType[]>([]);
  const { exportData, isExporting, error } = useExport();

  const handleQuickExport = (range: 'year' | 'month' | 'all') => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    let from: string | undefined;
    let to: string | undefined;

    if (range === 'year') {
      from = `${year}-01-01`;
      to = `${year}-12-31`;
    } else if (range === 'month') {
      const lastDay = new Date(year, month + 1, 0).getDate();
      from = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      to = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    }

    exportData({
      fromDate: from,
      toDate: to,
      includeDocumentTypes: ALL_DOCUMENT_TYPES,
    });
  };

  const handleCustomExport = () => {
    exportData({
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      includeDocumentTypes: selectedDocTypes.length > 0 ? selectedDocTypes : undefined,
    });
  };

  const toggleDocType = (type: DocumentType) => {
    setSelectedDocTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  return (
    <div className="p-8">
      <div className="max-w-[800px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-[28px] font-semibold text-[#1B365D] mb-1">Export Data</h1>
          <p className="text-[14px] text-[#718096]">Download your expense data and documents</p>
        </div>

        {/* Quick Export */}
        <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 mb-6">
          <h2 className="text-[16px] font-semibold text-[#1B365D] mb-2">Quick Export</h2>
          <p className="text-[13px] text-[#718096] mb-4">
            Download your expenses and all attached documents as a ZIP file
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="secondary"
              size="default"
              disabled={isExporting}
              onClick={() => handleQuickExport('year')}
            >
              {isExporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
              This Year
            </Button>
            <Button
              variant="secondary"
              size="default"
              disabled={isExporting}
              onClick={() => handleQuickExport('month')}
            >
              {isExporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
              This Month
            </Button>
            <Button
              variant="secondary"
              size="default"
              disabled={isExporting}
              onClick={() => handleQuickExport('all')}
            >
              {isExporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
              All Time
            </Button>
          </div>
        </div>

        {/* Custom Export */}
        <div className="bg-white rounded-lg border border-[#E2E8F0] p-6">
          <h2 className="text-[16px] font-semibold text-[#1B365D] mb-2">Custom Export</h2>
          <p className="text-[13px] text-[#718096] mb-4">
            Choose a date range and which document types to include
          </p>

          <div className="space-y-4 mb-6">
            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-semibold text-[#4A5568] mb-1.5">
                  From Date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full h-11 px-3 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D]"
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#4A5568] mb-1.5">
                  To Date
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full h-11 px-3 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D]"
                />
              </div>
            </div>

            {/* Document Types */}
            <div>
              <label className="block text-[13px] font-semibold text-[#4A5568] mb-2">
                Include Documents
              </label>
              <p className="text-[12px] text-[#718096] mb-3">
                Select document types to include. If none are selected, only the CSV data file will be downloaded.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ALL_DOCUMENT_TYPES.map((type) => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer py-1">
                    <input
                      type="checkbox"
                      checked={selectedDocTypes.includes(type)}
                      onChange={() => toggleDocType(type)}
                      className="w-4 h-4 rounded border-[#E2E8F0] text-[#1B365D] focus:ring-[#1B365D]"
                    />
                    <span className="text-[14px] text-[#4A5568]">{DOCUMENT_TYPE_LABELS[type]}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <Button
            variant="primary"
            size="default"
            disabled={isExporting}
            onClick={handleCustomExport}
          >
            {isExporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
            Export Custom Range
          </Button>
        </div>

        {/* Error */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-[14px] text-red-700">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
